import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, retryOperation, recoverSession } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user profile from database with retry logic
  const loadProfile = async (userId) => {
    try {
      const { data, error } = await retryOperation(
        () => supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
      );

      if (error) throw error;
      setProfile(data);
      setError(null);
    } catch (error) {
      console.error('Error loading profile:', error?.message);
      setProfile(null);
      
      // Only set error for non-connection issues
      if (!error?.message?.includes('connection') && !error?.code?.startsWith('57P')) {
        setError('Falha ao carregar perfil do usuário');
      }
    }
  };

  useEffect(() => {
    // Get initial session with retry logic
    const initializeAuth = async () => {
      try {
        const session = await recoverSession();
        setUser(session?.user ?? null);
        if (session?.user) {
          await loadProfile(session?.user?.id);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error?.message);
        setError('Erro ao inicializar autenticação. Tente recarregar a página.');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes (MUST be synchronous)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadProfile(session?.user?.id); // Fire-and-forget
      } else {
        setProfile(null);
      }
      
      // Clear errors on successful auth change
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setError(null);
      }
      
      // Handle token refresh errors
      if (event === 'TOKEN_REFRESH_FAILED') {
        console.error('Token refresh failed, attempting recovery...');
        try {
          await recoverSession();
        } catch (error) {
          console.error('Session recovery failed:', error?.message);
          setError('Sessão expirada. Por favor, faça login novamente.');
        }
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      setError(null);
      const { data, error } = await retryOperation(
        () => supabase?.auth?.signInWithPassword({ email, password })
      );
      if (error) throw error;
      return data;
    } catch (error) {
      const errorMessage = error?.message?.includes('Invalid login credentials')
        ? 'Email ou senha incorretos' : error?.message?.includes('connection')
        ? 'Erro de conexão. Tente novamente em alguns instantes.' :'Erro ao fazer login. Tente novamente.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email, password, fullName, role = 'cliente') => {
    try {
      setError(null);
      const { data, error } = await retryOperation(
        () => supabase?.auth?.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              role: role
            }
          }
        })
      );
      if (error) throw error;
      return data;
    } catch (error) {
      const errorMessage = error?.message?.includes('connection')
        ? 'Erro de conexão. Tente novamente em alguns instantes.' :'Erro ao criar conta. Tente novamente.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setProfile(null);
    } catch (error) {
      console.error('Sign out error:', error?.message);
      // Force clear local state even if API call fails
      setUser(null);
      setProfile(null);
      // Don't throw error for sign out - always succeed locally
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('Não autenticado');

    try {
      setError(null);
      const { data, error } = await retryOperation(
        () => supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      );

      if (error) throw error;
      setProfile(data);
      return data;
    } catch (error) {
      const errorMessage = error?.message?.includes('connection')
        ? 'Erro de conexão. Tente novamente em alguns instantes.' :'Erro ao atualizar perfil. Tente novamente.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const clearError = () => setError(null);

  const value = {
    user,
    profile,
    loading,
    error,
    clearError,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAdmin: profile?.role === 'admin',
    isClient: profile?.role === 'cliente'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};